/**
 * @module bridges
 * @package @geenius/adapters/solidjs-ark
 * @description Ark UI Solid bridge components for adapter loading, error, and
 * toast affordances. The bridge layer is additive; adapter contracts and
 * SolidJS primitives remain inherited from `@geenius/adapters/solidjs`.
 */

import { Field } from "@ark-ui/solid/field";
import { Progress } from "@ark-ui/solid/progress";
import { createToaster, Toast, Toaster } from "@ark-ui/solid/toast";
import { createEffect, ErrorBoundary, type JSX, Show } from "solid-js";

export type ToastBridgeType =
  | "error"
  | "info"
  | "loading"
  | "success"
  | "warning";
export type ToastPlacement =
  | "top-start"
  | "top"
  | "top-end"
  | "bottom-start"
  | "bottom"
  | "bottom-end";

export interface ToastOptions {
  id?: string;
  title?: JSX.Element;
  description?: JSX.Element;
  duration?: number;
  closable?: boolean;
}

export interface ToastBridgeToaster {
  error: (options: ToastOptions) => void;
  info: (options: ToastOptions) => void;
  loading: (options: ToastOptions) => void;
  success: (options: ToastOptions) => void;
  warning: (options: ToastOptions) => void;
}

const defaultLoadingLabel = "Loading adapter";
const defaultErrorTitle = "Adapter error";

function classNames(...classes: Array<string | undefined | false>): string {
  return classes.filter(Boolean).join(" ");
}

function clampProgress(value: number): number {
  return Math.max(0, Math.min(100, value));
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  return "The adapter bridge caught an unexpected error.";
}

function emitToast(
  toaster: ToastBridgeToaster,
  type: ToastBridgeType,
  options: ToastOptions,
): void {
  if (type === "error") {
    toaster.error(options);
    return;
  }
  if (type === "loading") {
    toaster.loading(options);
    return;
  }
  if (type === "success") {
    toaster.success(options);
    return;
  }
  if (type === "warning") {
    toaster.warning(options);
    return;
  }

  toaster.info(options);
}

/** Props for the Ark UI Solid loading bridge. */
export interface LoadingBridgeProps {
  /** Whether the bridge should render the loading indicator. */
  loading?: boolean;
  /** Accessible label and visible text for the progress state. */
  label?: string;
  /** Optional determinate progress value from 0 to 100. */
  value?: number;
  /** Optional class merged into the Ark Progress root. */
  class?: string;
  /** Content rendered when `loading` is false. */
  children?: JSX.Element;
}

/** Renders an Ark UI Progress primitive for async adapter loading states. */
export function LoadingBridge(props: LoadingBridgeProps): JSX.Element {
  const label = () => props.label ?? defaultLoadingLabel;
  const value = () =>
    typeof props.value === "number" ? clampProgress(props.value) : undefined;

  return (
    <Show when={props.loading ?? true} fallback={props.children ?? null}>
      <Progress.Root
        value={value() ?? null}
        max={100}
        class={classNames(
          "gn-adapters-ark-loading inline-flex w-full flex-col gap-2",
          props.class,
        )}
        data-gn-adapters-bridge="loading"
      >
        <div class="flex items-center justify-between gap-3 text-sm font-medium">
          <Progress.Label>{label()}</Progress.Label>
          <Show when={value() !== undefined}>
            <Progress.ValueText />
          </Show>
        </div>
        <Progress.Track
          aria-label={label()}
          class="h-2 overflow-hidden rounded-full bg-muted"
        >
          <Progress.Range class="h-full rounded-full bg-primary transition-[width]" />
        </Progress.Track>
      </Progress.Root>
    </Show>
  );
}

/** Props for the Ark UI Solid error boundary bridge. */
export interface ErrorBoundaryBridgeProps {
  /** Child tree protected by the SolidJS error boundary. */
  children?: JSX.Element;
  /** Optional externally supplied error, useful for async adapter failures. */
  error?: unknown;
  /** Heading rendered in the Ark field error surface. */
  title?: string;
  /** Optional class merged into the Ark Field root. */
  class?: string;
  /** Callback fired when the bridge observes an error. */
  onError?: (error: unknown) => void;
  /** Custom fallback renderer for teams that need a branded error surface. */
  fallback?: (error: unknown, reset: () => void) => JSX.Element;
}

function ErrorFallback(props: {
  error: unknown;
  title?: string;
  class?: string;
  reset?: () => void;
  onError?: (error: unknown) => void;
}): JSX.Element {
  createEffect(() => {
    props.onError?.(props.error);
  });

  return (
    <Field.Root
      invalid
      role="alert"
      class={classNames(
        "gn-adapters-ark-error rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive",
        props.class,
      )}
      data-gn-adapters-bridge="error"
    >
      <Field.Label class="font-semibold">
        {props.title ?? defaultErrorTitle}
      </Field.Label>
      <Field.ErrorText class="mt-1 block">
        {errorMessage(props.error)}
      </Field.ErrorText>
      <Show when={props.reset}>
        {(reset) => (
          <button
            type="button"
            class="mt-3 inline-flex rounded-md border border-current px-3 py-1 text-xs font-medium"
            onClick={() => reset()()}
          >
            Retry
          </button>
        )}
      </Show>
    </Field.Root>
  );
}

function CustomErrorFallback(props: {
  error: unknown;
  reset: () => void;
  fallback: (error: unknown, reset: () => void) => JSX.Element;
  onError?: (error: unknown) => void;
}): JSX.Element {
  createEffect(() => {
    props.onError?.(props.error);
  });

  return <>{props.fallback(props.error, props.reset)}</>;
}

/** Wraps children in a SolidJS ErrorBoundary and renders failures with Ark UI Field primitives. */
export function ErrorBoundaryBridge(
  props: ErrorBoundaryBridgeProps,
): JSX.Element {
  if (props.error !== undefined) {
    return props.fallback ? (
      <CustomErrorFallback
        error={props.error}
        reset={() => undefined}
        fallback={props.fallback}
        onError={props.onError}
      />
    ) : (
      <ErrorFallback
        error={props.error}
        title={props.title}
        class={props.class}
        onError={props.onError}
      />
    );
  }

  return (
    <ErrorBoundary
      fallback={(error: unknown, reset) =>
        props.fallback ? (
          <CustomErrorFallback
            error={error}
            reset={reset}
            fallback={props.fallback}
            onError={props.onError}
          />
        ) : (
          <ErrorFallback
            error={error}
            title={props.title}
            class={props.class}
            reset={reset}
            onError={props.onError}
          />
        )
      }
    >
      {props.children}
    </ErrorBoundary>
  );
}

/** Props for the Ark UI Solid toast bridge. */
export interface ToastBridgeProps {
  /** Toast title emitted when the bridge is opened. */
  title?: JSX.Element;
  /** Toast body emitted when the bridge is opened. */
  description?: JSX.Element;
  /** Toast tone mapped to the Ark toaster store helpers. */
  type?: ToastBridgeType;
  /** Controls emission; defaults to true when title or description is present. */
  open?: boolean;
  /** Optional stable toast id for update/dedupe behavior. */
  id?: string;
  /** Toast auto-dismiss duration in milliseconds. */
  duration?: number;
  /** Whether Ark renders a close trigger for the toast. */
  closable?: boolean;
  /** Ark toaster placement. */
  placement?: ToastPlacement;
  /** Optional injected Ark toaster for app-level toast orchestration. */
  toaster?: ToastBridgeToaster;
  /** Optional class merged into the Ark Toaster region. */
  class?: string;
  /** Content rendered next to the toaster region. */
  children?: JSX.Element;
}

const toastToneClasses: Record<ToastBridgeType, string> = {
  error: "border-destructive/30 bg-destructive/10 text-destructive",
  info: "border-border bg-background text-foreground",
  loading: "border-border bg-background text-foreground",
  success: "border-emerald-500/30 bg-emerald-50 text-emerald-950",
  warning: "border-amber-500/40 bg-amber-50 text-amber-950",
};

function isToastBridgeType(value: unknown): value is ToastBridgeType {
  return typeof value === "string" && value in toastToneClasses;
}

function toastToneClass(value: unknown): string {
  return toastToneClasses[isToastBridgeType(value) ? value : "info"];
}

/** Renders an Ark UI Toaster and optionally emits an adapter notification. */
export function ToastBridge(props: ToastBridgeProps): JSX.Element {
  const arkToaster = createToaster({
    placement: props.placement ?? "bottom-end",
  });
  const toaster = props.toaster ?? arkToaster;
  let emittedKey: string | undefined;

  createEffect(() => {
    const shouldOpen =
      props.open ??
      (props.title !== undefined || props.description !== undefined);

    if (!shouldOpen) {
      return;
    }

    const type = props.type ?? "info";
    const key = [
      props.id ?? "auto",
      type,
      String(props.title ?? ""),
      String(props.description ?? ""),
    ].join(":");

    if (emittedKey === key) {
      return;
    }

    emittedKey = key;
    emitToast(toaster, type, {
      id: props.id ?? key,
      title: props.title,
      description: props.description,
      duration: props.duration,
      closable: props.closable ?? true,
    });
  });

  return (
    <>
      {props.children}
      <Show when={props.toaster === undefined}>
        <Toaster
          toaster={arkToaster}
          class={classNames(
            "gn-adapters-ark-toaster fixed z-50 flex flex-col gap-2",
            props.class,
          )}
          data-gn-adapters-bridge="toast"
          data-gn-adapters-placement={props.placement ?? "bottom-end"}
        >
          {(toast) => (
            <Toast.Root
              class={classNames(
                "rounded-md border p-4 text-sm shadow-lg",
                toastToneClass(toast().type),
              )}
            >
              <div class="grid gap-1">
                <Show when={toast().title}>
                  <Toast.Title class="font-semibold">
                    {toast().title}
                  </Toast.Title>
                </Show>
                <Show when={toast().description}>
                  <Toast.Description class="text-muted-foreground">
                    {toast().description}
                  </Toast.Description>
                </Show>
              </div>
              <Show when={toast().closable}>
                <Toast.CloseTrigger class="mt-3 text-xs font-medium">
                  Dismiss
                </Toast.CloseTrigger>
              </Show>
            </Toast.Root>
          )}
        </Toaster>
      </Show>
    </>
  );
}
